import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import {
  getToken,
  getRefreshToken,
  updateAccessToken,
  clearSession,
} from '@/lib/auth/session';
import {
  AUTH_SESSION_EXPIRED_EVENT,
  AUTH_FORBIDDEN_EVENT,
} from '@/constants/auth.constants';

// API Response types following Single Responsibility Principle
export interface ApiResponse<T = unknown> {
  data: T;
  message?: string;
  success: boolean;
}

export interface ApiError {
  message: string;
  code?: string;
  errors?: Record<string, string[]>;
}

// Axios request config + our one-shot retry flag, so a request is only ever
// re-issued once after a refresh (prevents refresh loops on a repeat 401).
interface RetriableRequest extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

// Endpoints where a 401 is a credential error to surface to the caller (e.g. the
// login form), NOT an expired session to refresh/log out. The /auth/refresh call
// itself uses bare axios below, so it never reaches this interceptor.
const isAuthEndpoint = (url?: string): boolean =>
  !!url && (url.includes('/auth/login') || url.includes('/auth/logout'));

function emit(eventName: string, message?: string): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(eventName, { detail: { message } }));
}

// API Client configuration
export interface ApiClientConfig {
  baseURL: string;
}

// Main API Client class following Single Responsibility
export class ApiClient {
  private client: AxiosInstance;
  // Single-flight refresh: concurrent 401s all await the SAME refresh call
  // instead of stampeding /auth/refresh, and all share its resolution/rejection.
  private refreshPromise: Promise<string> | null = null;

  constructor(config: ApiClientConfig) {
    this.client = axios.create({
      baseURL: config.baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Request interceptor - attach the current access token
    this.client.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        const token = getToken();
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor - refresh-on-401, surface-403, format 400s
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError<ApiError>) => {
        const originalRequest = error.config as RetriableRequest | undefined;
        const status = error.response?.status;

        // 401 Unauthorized → try a silent refresh, then end the session.
        if (status === 401 && originalRequest) {
          // A 401 from login/logout is a credential error — let the caller show it.
          if (isAuthEndpoint(originalRequest.url)) {
            return Promise.reject(error);
          }

          // Already retried once, or nothing to refresh with → session is over.
          if (originalRequest._retry || !getRefreshToken()) {
            this.endSession();
            return Promise.reject(error);
          }

          originalRequest._retry = true;
          try {
            const newToken = await this.refreshAccessToken();
            originalRequest.headers = originalRequest.headers ?? {};
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return this.client(originalRequest);
          } catch (refreshError) {
            this.endSession();
            return Promise.reject(refreshError);
          }
        }

        // 403 Forbidden → authenticated but not permitted. Never log out; surface it.
        if (status === 403) {
          const message =
            error.response?.data?.message ??
            'You do not have permission to perform this action.';
          emit(AUTH_FORBIDDEN_EVENT, message);
          return Promise.reject(error);
        }

        // 400 Bad Request → format backend validation errors for display.
        if (status === 400 && error.response?.data?.errors) {
          const formattedErrors = Object.entries(error.response.data.errors)
            .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
            .join('; ');
          error.message = `Validation failed: ${formattedErrors}`;
        }

        return Promise.reject(error);
      }
    );
  }

  // Clear persisted auth and tell the React tree the session has ended. The
  // AuthProvider listens for this event and handles the redirect to /login.
  private endSession(): void {
    clearSession();
    emit(AUTH_SESSION_EXPIRED_EVENT, 'Your session has expired. Please sign in again.');
  }

  private refreshAccessToken(): Promise<string> {
    if (!this.refreshPromise) {
      this.refreshPromise = this.performRefresh().finally(() => {
        this.refreshPromise = null;
      });
    }
    return this.refreshPromise;
  }

  private async performRefresh(): Promise<string> {
    const refreshToken = getRefreshToken();
    if (!refreshToken) throw new Error('No refresh token available');

    // Bare axios (NOT this.client) so the refresh can't recurse through interceptors.
    const response = await axios.post<ApiResponse<{ accessToken: string }>>(
      `${this.client.defaults.baseURL}/auth/refresh`,
      { refreshToken }
    );

    const newToken = response.data?.data?.accessToken;
    if (!newToken) throw new Error('Refresh response missing access token');

    updateAccessToken(newToken);
    return newToken;
  }

  // Generic HTTP methods following DRY principle
  async get<T>(url: string, config = {}): Promise<ApiResponse<T>> {
    const response = await this.client.get<ApiResponse<T>>(url, config);
    return response.data;
  }

  async post<T>(url: string, data?: unknown, config = {}): Promise<ApiResponse<T>> {
    const response = await this.client.post<ApiResponse<T>>(url, data, config);
    return response.data;
  }

  async put<T>(url: string, data?: unknown, config = {}): Promise<ApiResponse<T>> {
    const response = await this.client.put<ApiResponse<T>>(url, data, config);
    return response.data;
  }

  async patch<T>(url: string, data?: unknown, config = {}): Promise<ApiResponse<T>> {
    const response = await this.client.patch<ApiResponse<T>>(url, data, config);
    return response.data;
  }

  async delete<T>(url: string, config = {}): Promise<ApiResponse<T>> {
    const response = await this.client.delete<ApiResponse<T>>(url, config);
    return response.data;
  }

  // Get raw axios instance for special cases
  getAxiosInstance(): AxiosInstance {
    return this.client;
  }
}

// Factory function to create API client instance
export const createApiClient = (baseURL: string): ApiClient => {
  return new ApiClient({ baseURL });
};

// Default API client instance
export const apiClient = createApiClient(
  import.meta.env.VITE_API_URL || 'http://localhost:4000/api'
);